import React, { useState, useRef, useEffect, ReactNode } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Popper } from "@mui/material";
import "../../styles/ViewSwitcherPill.css";

export interface ViewSwitcherOption<T = string> {
  label: string;
  value: T;
  icon?: ReactNode;
}

export interface ViewSwitcherPillProps<T = string> {
  options: ViewSwitcherOption<T>[];
  value: T;
  onChange: (value: T) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  pillColor?: string;
}

const ViewSwitcherPill = <T extends string = string>({
  options = [],
  value,
  onChange,
  placeholder = "Select View",
  className = "",
  disabled = false,
  pillColor,
}: ViewSwitcherPillProps<T>) => {
  const [open, setOpen] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const isInsidePill = pillRef.current?.contains(e.target as Node);
      const isInsidePopper = popperRef.current?.contains(e.target as Node);
      if (!isInsidePill && !isInsidePopper) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setOpen((prev) => !prev);
    }
  };

  const handleSelect = (val: T) => {
    onChange(val);
    setOpen(false);
  };

  return (
    <div className={`vsp-container ${className}`}>
      <div
        ref={pillRef}
        className={`vsp-pill ${open ? "open" : ""} ${disabled ? "disabled" : ""}`}
        onClick={handleToggle}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selectedOption?.icon && (
          <span
            className="vsp-pill-icon"
            style={pillColor ? { color: pillColor } : undefined}
          >
            {selectedOption.icon}
          </span>
        )}
        <span
          className="vsp-pill-label"
          style={pillColor ? { color: pillColor } : undefined}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <KeyboardArrowDownIcon fontSize="small" className="vsp-pill-arrow" />
      </div>

      <Popper
        open={open}
        anchorEl={pillRef.current}
        placement="bottom-start"
        disablePortal={false}
        sx={{ zIndex: 1300 }}
        modifiers={[
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
              padding: 8,
            },
          },
          {
            name: "flip",
            options: {
              fallbackPlacements: ["top-start"],
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 4],
            },
          },
        ]}
      >
        <div ref={popperRef} className="vsp-dropdown" role="listbox">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={String(opt.value)}
                className={`vsp-option ${isSelected ? "selected" : ""}`}
                onClick={() => handleSelect(opt.value)}
                role="option"
                aria-selected={isSelected}
              >
                <div className="vsp-option-left">
                  {opt.icon && <span className="vsp-option-icon">{opt.icon}</span>}
                  <span className="vsp-option-text">{opt.label}</span>
                </div>
                <span
                  className={`vsp-option-circle ${
                    isSelected ? "filled" : "empty"
                  }`}
                />
              </div>
            );
          })}
        </div>
      </Popper>
    </div>
  );
};

export default ViewSwitcherPill;
